import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PINEntry from './PINEntry';
import { useWallet } from '../../contexts/WalletContext';

const contacts = [
  { id: 1, name: 'Youssef El Amrani', simoId: 'youssef.amrani@simopay', color: '#4F46E5', recent: true },
  { id: 2, name: 'Fatima Zahra', simoId: 'fatima.zahra@simopay', color: '#059669', recent: true },
  { id: 3, name: 'Hassan Benali', simoId: 'hassan.benali@simopay', color: '#DC2626', recent: true },
  { id: 4, name: 'Nadia Alaoui', simoId: 'nadia.alaoui@simopay', color: '#D97706', recent: false },
  { id: 5, name: 'Karim Tahiri', simoId: 'karim.tahiri@simopay', color: '#7C3AED', recent: false },
];

// Responsive font size for amount based on length
const getAmountFontSize = (val) => {
  const len = String(val || '').length;
  if (len > 7) return '26px';
  if (len > 5) return '32px';
  if (len > 3) return '38px';
  return '42px';
};

const SendMoney = ({ onBack, onSuccess, onNavigate, t, isRTL }) => {
  const [query, setQuery] = useState('');
  const [amount, setAmount] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [step, setStep] = useState('select'); // 'select' | 'amount' | 'pin'
  const [sending, setSending] = useState(false);
  const { sendMoney, balance } = useWallet();

  const tr = t || {
    sendMoney: 'Send Money', enterSimoId: 'Search by name or @SimoID',
    scanQRPay: 'Pay via QR instead', recentContacts: 'Recent',
    allContacts: 'All Contacts', simoId: 'SimoPay ID',
    enterAmount: 'Enter Amount', sendNow: 'Send Now', back: '←',
  };

  const filtered = contacts.filter(
    (c) =>
      query === '' ||
      c.name.toLowerCase().includes(query.toLowerCase()) ||
      c.simoId.toLowerCase().includes(query.toLowerCase())
  );

  const handleContactSelect = (c) => {
    setSelectedContact(c);
    setStep('amount');
    setQuery('');
  };

  const handleSend = () => {
    if (!amount || parseFloat(amount) <= 0) return;
    setStep('pin');
  };

  const handlePINSuccess = () => {
    setSending(true);
    setTimeout(() => {
      // Apply the transfer to the wallet (decreases balance + adds debit transaction)
      sendMoney(amount, selectedContact);
      setSending(false);
      const toastMsg = typeof tr.toastSendSuccess === 'function'
        ? tr.toastSendSuccess(amount, selectedContact.simoId)
        : `Sent ${amount} MAD to ${selectedContact.simoId}`;
      onSuccess(toastMsg);
    }, 1400);
  };

  return (
    <div
      data-testid="send-screen"
      className="no-scrollbar"
      style={{
        background: '#0F0F0F',
        height: '100%',
        fontFamily: 'Inter, sans-serif',
        overflowY: 'auto',
        paddingBottom: '12px',
        direction: isRTL ? 'rtl' : 'ltr',
        position: 'relative',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '18px 20px 14px' }}>
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={onBack}
          style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#F5F1EA', fontSize: '16px', flexShrink: 0 }}
        >
          {isRTL ? '→' : '←'}
        </motion.button>
        <h2 style={{ color: '#F5F1EA', fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.4px' }}>{tr.sendMoney}</h2>
      </div>

      <AnimatePresence mode="wait">
        {step === 'select' && (
          <motion.div
            key="select"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.25 }}
            style={{ padding: '0 20px' }}
          >
            {/* SimoPay ID search */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '14px',
                padding: '10px 14px',
                marginBottom: '14px',
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#8B93A7" strokeWidth="2" strokeLinecap="round">
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                data-testid="simo-id-search"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={tr.enterSimoId}
                style={{
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  color: '#F5F1EA',
                  fontSize: '13px',
                  flex: 1,
                  fontFamily: 'Inter, sans-serif',
                }}
              />
            </div>

            {/* Pay via QR option */}
            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={() => onNavigate('qrscan')}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 14px',
                background: 'rgba(212,175,55,0.04)',
                border: '1px solid rgba(212,175,55,0.12)',
                borderRadius: '14px',
                marginBottom: '20px',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontSize: '18px' }}>⊡</span>
              <span style={{ color: '#D4AF37', fontSize: '13px', fontWeight: '600' }}>{tr.scanQRPay}</span>
            </motion.button>

            {/* Recent SimoPay contacts */}
            <p style={{ color: '#8B93A7', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
              {tr.recentContacts}
            </p>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '22px', overflowX: 'auto', paddingBottom: '4px' }} className="no-scrollbar">
              {contacts.filter(c => c.recent).map((c) => (
                <motion.button
                  key={c.id}
                  whileTap={{ scale: 0.94 }}
                  onClick={() => handleContactSelect(c)}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0 }}
                >
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700', color: '#fff' }}>{c.name[0]}</div>
                  <span style={{ color: '#8B93A7', fontSize: '10px', maxWidth: '52px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name.split(' ')[0]}</span>
                </motion.button>
              ))}
            </div>

            {/* All contacts */}
            <p style={{ color: '#8B93A7', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>
              {tr.allContacts}
            </p>
            {filtered.map((c, i) => (
              <motion.div
                key={c.id}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleContactSelect(c)}
                style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: i < filtered.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', cursor: 'pointer' }}
              >
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>{c.name[0]}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ color: '#F5F1EA', fontSize: '13px', fontWeight: '600', margin: 0 }}>{c.name}</p>
                  <p style={{ color: '#8B93A7', fontSize: '11px', margin: 0, letterSpacing: '-0.2px' }}>{c.simoId}</p>
                </div>
                <span style={{ color: '#8B93A7', fontSize: '14px', flexShrink: 0 }}>→</span>
              </motion.div>
            ))}
            {filtered.length === 0 && (
              <p style={{ color: '#8B93A7', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No contacts found</p>
            )}
          </motion.div>
        )}

        {step === 'amount' && (
          <motion.div
            key="amount"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -16 }}
            transition={{ duration: 0.25 }}
            style={{ padding: '0 20px' }}
          >
            {/* Selected contact */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)', borderRadius: '18px', marginBottom: '24px' }}>
              <div style={{ width: '46px', height: '46px', borderRadius: '50%', background: selectedContact?.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: '700', color: '#fff', flexShrink: 0 }}>{selectedContact?.name[0]}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: '#F5F1EA', fontSize: '14px', fontWeight: '600', margin: 0, letterSpacing: '-0.2px' }}>{selectedContact?.name}</p>
                <p style={{ color: '#D4AF37', fontSize: '12px', margin: '2px 0 0', fontWeight: '500', letterSpacing: '0.1px' }}>{selectedContact?.simoId}</p>
              </div>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={() => setStep('select')}
                style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '9px', padding: '5px 10px', color: '#8B93A7', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}
              >
                Change
              </motion.button>
            </div>

            {/* Amount input — fixed short golden line, amount centered above with currency on right */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '32px' }}>
              <p style={{ color: '#8B93A7', fontSize: '11px', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '0.8px', fontWeight: '600' }}>{tr.enterAmount}</p>

              {/* Amount + currency — flex group, centered, above the golden line */}
              <label
                htmlFor="send-amount-input"
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'center',
                  gap: '10px',
                  height: '56px',
                  cursor: 'text',
                  width: '100%',
                  maxWidth: '280px',
                }}
              >
                <input
                  id="send-amount-input"
                  data-testid="amount-input"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={amount}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    if (val.length <= 7) setAmount(val);
                  }}
                  placeholder="0"
                  style={{
                    background: 'none',
                    border: 'none',
                    outline: 'none',
                    color: amount ? '#F5F1EA' : '#5C6478',
                    fontSize: getAmountFontSize(amount || '0'),
                    fontWeight: '800',
                    letterSpacing: '-1.5px',
                    textAlign: 'right',
                    width: 'auto',
                    minWidth: '40px',
                    maxWidth: '180px',
                    fontFamily: 'Inter, sans-serif',
                    transition: 'font-size 0.15s ease, color 0.2s ease',
                    padding: 0,
                  }}
                  size={Math.max(1, (amount || '0').length)}
                />
                <span style={{ color: '#8B93A7', fontSize: '17px', fontWeight: '500', flexShrink: 0 }}>MAD</span>
              </label>

              {/* Constant short golden line — fixed width, always visible */}
              <div
                style={{
                  width: '88px',
                  height: '2px',
                  background: 'linear-gradient(90deg, rgba(212,175,55,0.15), rgba(212,175,55,0.85), rgba(212,175,55,0.15))',
                  borderRadius: '2px',
                  marginTop: '6px',
                  boxShadow: '0 0 12px rgba(212,175,55,0.35)',
                }}
              />
            </div>

            {/* Quick amount presets */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '28px' }}>
              {[50, 100, 250, 500].map((val) => (
                <motion.button
                  key={val}
                  whileTap={{ scale: 0.93 }}
                  onClick={() => setAmount(String(val))}
                  style={{ padding: '6px 12px', borderRadius: '100px', background: amount === String(val) ? 'rgba(212,175,55,0.12)' : 'rgba(255,255,255,0.04)', border: amount === String(val) ? '1px solid rgba(212,175,55,0.25)' : '1px solid rgba(255,255,255,0.07)', color: amount === String(val) ? '#D4AF37' : '#8B93A7', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                >
                  {val}
                </motion.button>
              ))}
            </div>

            <motion.button
              data-testid="send-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleSend}
              disabled={!amount || parseFloat(amount) <= 0 || sending}
              style={{ width: '100%', background: amount && parseFloat(amount) > 0 ? 'linear-gradient(135deg, #D4AF37, #B8962E)' : 'rgba(255,255,255,0.06)', color: amount && parseFloat(amount) > 0 ? '#0F0F0F' : '#8B93A7', border: 'none', borderRadius: '16px', padding: '15px', fontSize: '15px', fontWeight: '700', cursor: amount ? 'pointer' : 'not-allowed', transition: 'all 0.25s' }}
            >
              {sending ? 'Sending...' : `${tr.sendNow}${amount ? ` → ${amount} MAD` : ''}`}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PIN Entry — full screen change */}
      {step === 'pin' && (
        <PINEntry
          amount={amount}
          recipient={selectedContact?.name}
          mode="send"
          onSuccess={handlePINSuccess}
          onCancel={() => setStep('amount')}
          t={t}
          isRTL={isRTL}
        />
      )}
    </div>
  );
};

export default SendMoney;
